import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Brand from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Check } from "lucide-react";

const AUTH_IMG = "https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwY2xpbmljJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg2NjU2MTc2fDA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", city: "", profile_bio: "", plan_id: "",
  });
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/plans").then(({ data }) => {
      const active = data.filter((p) => p.active);
      setPlans(active);
      if (active[0]) setForm((f) => ({ ...f, plan_id: active[0].id }));
    });
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { setError("É necessário aceitar os Termos e a Política de Privacidade."); return; }
    setError(""); setLoading(true);
    try {
      await register({ ...form, role: "therapist", consent });
      navigate("/painel");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="w-full max-w-md mx-auto">
          <Link to="/"><Brand className="mb-8" /></Link>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Crie sua plataforma</h1>
          <p className="text-muted-foreground mt-2">Cadastro de hipnoterapeuta. Após o cadastro, seu acesso passa por aprovação.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nome profissional</Label>
                <Input id="name" value={form.name} onChange={set("name")} required data-testid="reg-name-input" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="phone">Telefone</Label>
                <Input id="phone" value={form.phone} onChange={set("phone")} data-testid="reg-phone-input" className="mt-1.5" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="reg-email-input" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="city">Cidade</Label>
                <Input id="city" value={form.city} onChange={set("city")} data-testid="reg-city-input" className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <Input id="password" type="password" value={form.password} onChange={set("password")} required data-testid="reg-password-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="bio">Perfil profissional</Label>
              <Textarea id="bio" value={form.profile_bio} onChange={set("profile_bio")} data-testid="reg-bio-input" className="mt-1.5" rows={2} placeholder="Conte um pouco sobre sua atuação..." />
            </div>
            <div>
              <Label>Plano escolhido</Label>
              <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {plans.map((p) => (
                  <button type="button" key={p.id} onClick={() => setForm({ ...form, plan_id: p.id })}
                    data-testid={`reg-plan-${p.name.toLowerCase()}`}
                    className={`text-left rounded-xl border p-3 transition-colors ${form.plan_id === p.id ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:border-primary/40"}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{p.name}</span>
                      {form.plan_id === p.id && <Check className="w-4 h-4 text-primary" />}
                    </div>
                    <div className="text-primary font-heading font-semibold mt-1">R$ {p.price.toFixed(2)}</div>
                    <div className="text-[11px] text-muted-foreground">{p.periodicity}</div>
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm text-muted-foreground">
              <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="reg-consent-checkbox" className="mt-0.5" />
              <span>Li e aceito os Termos de Uso e a Política de Privacidade (LGPD).</span>
            </label>
            {error && <p className="text-sm text-destructive" data-testid="reg-error">{error}</p>}
            <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="reg-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar minha conta"}
            </Button>
          </form>

          <p className="mt-5 text-sm text-muted-foreground text-center">
            Já tem conta?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="go-login-link">Entrar</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img src={AUTH_IMG} alt="Clínica minimalista" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/40" />
      </div>
    </div>
  );
}
